import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

export default function CSS_Transitions() {
  return (
    <div className="tutorial-container">
      {/* Title */}
      <h1 className="tutorial-title">⏱️ Advanced CSS: Transitions</h1>

      {/* Intro */}
      <p className="tutorial-intro">
        CSS Transitions allow you to smoothly change property values over a
        specified duration. They are perfect for hover effects, interactive UI
        elements, and subtle animations without using JavaScript.
      </p>

      {/* Key Points */}
      <div
        style={{
          background: "linear-gradient(135deg, #e0f2fe, #bae6fd)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginBottom: "2rem",
        }}
      >
        <h2 style={{ color: "#075985" }}>🔑 Key Points</h2>
        <ul className="tutorial-list-points">
          <li>
            <code>transition-property</code> specifies the CSS property to
            animate.
          </li>
          <li>
            <code>transition-duration</code> sets how long the transition takes.
          </li>
          <li>
            <code>transition-timing-function</code> controls the speed curve
            (ease, linear, etc.).
          </li>
          <li>
            <code>transition-delay</code> delays the start of a transition.
          </li>
          <li>
            Multiple properties can transition simultaneously, separated by
            commas.
          </li>
        </ul>
      </div>

      {/* Basic Example */}
      <div className="tutorial-section">
        <h2 className="section-title">✨ Basic Transition</h2>
        <p>Hover over the box to see the background color change smoothly:</p>
        <CodeCopyBlock
          code={`div {
  width: 150px;
  height: 150px;
  background-color: #3b82f6;
  transition: background-color 0.5s ease;
}
div:hover {
  background-color: #10b981;
}`}
        />
        <div
          style={{
            width: "150px",
            height: "150px",
            backgroundColor: "#3b82f6",
            margin: "1rem",
            borderRadius: "8px",
            transition: "background-color 0.5s ease",
          }}
          onMouseEnter={(e) =>
            (e.currentTarget.style.backgroundColor = "#10b981")
          }
          onMouseLeave={(e) =>
            (e.currentTarget.style.backgroundColor = "#3b82f6")
          }
        />
      </div>

      {/* Multiple Properties */}
      <div className="tutorial-section">
        <h2 className="section-title">🔄 Multiple Properties</h2>
        <p>Transition both background and transform on hover:</p>
        <CodeCopyBlock
          code={`div {
  width: 120px;
  height: 120px;
  background-color: #ef4444;
  transition: background-color 0.5s, transform 0.5s;
}
div:hover {
  background-color: #fbbf24;
  transform: scale(1.2);
}`}
        />
        <div
          style={{
            width: "120px",
            height: "120px",
            backgroundColor: "#ef4444",
            margin: "1rem",
            borderRadius: "8px",
            transition: "background-color 0.5s, transform 0.5s",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = "#fbbf24";
            e.currentTarget.style.transform = "scale(1.2)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = "#ef4444";
            e.currentTarget.style.transform = "scale(1)";
          }}
        />
      </div>

      {/* Timing Functions */}
      <div className="tutorial-section">
        <h2 className="section-title">⏱️ Timing Functions</h2>
        <p>Use different easing functions for custom effects:</p>
        <CodeCopyBlock
          code={`div {
  width: 150px;
  height: 150px;
  background-color: #8b5cf6;
  transition: transform 0.8s cubic-bezier(0.68, -0.55, 0.27, 1.55);
}
div:hover {
  transform: rotate(20deg);
}`}
        />
        <div
          style={{
            width: "150px",
            height: "150px",
            backgroundColor: "#8b5cf6",
            margin: "1rem",
            borderRadius: "8px",
            transition: "transform 0.8s cubic-bezier(0.68, -0.55, 0.27, 1.55)",
          }}
          onMouseEnter={(e) =>
            (e.currentTarget.style.transform = "rotate(20deg)")
          }
          onMouseLeave={(e) =>
            (e.currentTarget.style.transform = "rotate(0deg)")
          }
        />
      </div>

      {/* Delays */}
      <div className="tutorial-section">
        <h2 className="section-title">⏳ Transition Delay</h2>
        <p>Add a delay before the transition starts:</p>
        <CodeCopyBlock
          code={`div {
  width: 120px;
  height: 120px;
  background-color: #f97316;
  transition: transform 0.5s ease 0.3s;
}
div:hover {
  transform: scale(1.3);
}`}
        />
        <div
          style={{
            width: "120px",
            height: "120px",
            backgroundColor: "#f97316",
            margin: "1rem",
            borderRadius: "8px",
            transition: "transform 0.5s ease 0.3s",
          }}
          onMouseEnter={(e) => (e.currentTarget.style.transform = "scale(1.3)")}
          onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
        />
      </div>

      {/* More Examples */}
      <div className="tutorial-section">
        <h2 className="section-title">✨ More Practical Examples</h2>

        {/* Opacity Transition */}
        <h3 className="section-subtitle">1. Opacity Fade</h3>
        <CodeCopyBlock
          code={`div {
  width: 150px;
  height: 150px;
  background-color: #3b82f6;
  transition: opacity 0.7s ease;
}
div:hover {
  opacity: 0.5;
}`}
        />
        <div
          style={{
            width: "150px",
            height: "150px",
            backgroundColor: "#3b82f6",
            margin: "1rem",
            borderRadius: "8px",
            transition: "opacity 0.7s ease",
          }}
          onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.5")}
          onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
        />

        {/* Border Radius */}
        <h3 className="section-subtitle">2. Border Radius Morph</h3>
        <CodeCopyBlock
          code={`div {
  width: 120px;
  height: 120px;
  background-color: #10b981;
  transition: border-radius 0.5s ease;
}
div:hover {
  border-radius: 50%;
}`}
        />
        <div
          style={{
            width: "120px",
            height: "120px",
            backgroundColor: "#10b981",
            margin: "1rem",
            borderRadius: "8px",
            transition: "border-radius 0.5s ease",
          }}
          onMouseEnter={(e) => (e.currentTarget.style.borderRadius = "50%")}
          onMouseLeave={(e) => (e.currentTarget.style.borderRadius = "8px")}
        />

        {/* Box Shadow */}
        <h3 className="section-subtitle">3. Box Shadow Hover</h3>
        <CodeCopyBlock
          code={`div {
  width: 140px;
  height: 140px;
  background-color: #f59e0b;
  transition: box-shadow 0.5s ease;
}
div:hover {
  box-shadow: 0 10px 20px rgba(0,0,0,0.3);
}`}
        />
        <div
          style={{
            width: "140px",
            height: "140px",
            backgroundColor: "#f59e0b",
            margin: "1rem",
            borderRadius: "8px",
            transition: "box-shadow 0.5s ease",
          }}
          onMouseEnter={(e) =>
            (e.currentTarget.style.boxShadow = "0 10px 20px rgba(0,0,0,0.3)")
          }
          onMouseLeave={(e) => (e.currentTarget.style.boxShadow = "none")}
        />
      </div>

      {/* Best Practices */}
      <div className="tutorial-section">
        <h2 className="section-title">✅ Best Practices</h2>
        <ul className="tutorial-list-points">
          <li>
            Use transitions on hover for subtle, not distracting, effects.
          </li>
          <li>
            Prefer GPU-accelerated properties like <code>transform</code> and{" "}
            <code>opacity</code>.
          </li>
          <li>
            Combine transitions with 2D/3D transforms for interactive UIs.
          </li>
          <li>
            Avoid animating expensive properties like <code>width</code> or{" "}
            <code>height</code> for performance.
          </li>
        </ul>
      </div>
      {/* Translate / Move Example */}
      <h3 className="section-subtitle">4. Move Element</h3>
      <CodeCopyBlock
        code={`div {
  width: 120px;
  height: 120px;
  background-color: #8b5cf6;
  transition: transform 0.7s ease;
}
div:hover {
  transform: translate(100px, 50px);
}`}
      />
      <div
        style={{
          width: "120px",
          height: "120px",
          backgroundColor: "#8b5cf6",
          margin: "1rem",
          borderRadius: "8px",
          transition: "transform 0.7s ease",
          position: "relative", // required for translate movement
        }}
        onMouseEnter={(e) =>
          (e.currentTarget.style.transform = "translate(100px, 50px)")
        }
        onMouseLeave={(e) =>
          (e.currentTarget.style.transform = "translate(0, 0)")
        }
      />

      {/* Next Steps */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef3c7, #fde68a)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginTop: "2rem",
        }}
      >
        <h2 style={{ color: "#92400e" }}>🚀 Next Steps</h2>
        <ul className="tutorial-list-points">
          <li>
            Combine <b>transitions with keyframe animations</b> for advanced
            effects.
          </li>
          <li>
            Experiment with <b>hover + transform + opacity</b> for UI feedback.
          </li>
          <li>
            Use <b>transition-delay</b> to create staggered animations.
          </li>
          <li>
            Explore <b>interactive menus</b> and buttons using CSS transitions.
          </li>
          <li>
            Combine transitions with <b>3D transforms</b> for immersive effects.
          </li>
        </ul>
      </div>
    </div>
  );
}
